import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import moment from 'moment';
import { API, APIRoutes } from '@api';
import { calendarStore, routerStore, widgetStore, bookingStore } from '@stores';
import routes from '@routes';
import { formatDate } from '@utils/formatters';
import { logEvent, EVENTS_NAMES } from '@utils/analytics';
import { sendAnalyticsData, POST_MESSAGE_KEYS } from '@utils/communication';

const RESET_CALENDAR_ON_CHANGE_DATES = false;

export class SummaryStore {
  isLoading = false;

  summary = {};

  errors = {};

  isBookingAvailable = false;

  constructor() {
    makeAutoObservable(this);
  }

  getBookingDetails = async () => {
    try {
      const { startDate, endDate, yachtIds } = calendarStore;
      if (
        !startDate ||
        !endDate ||
        (typeof startDate.isValid === 'function' &&
          (!startDate.isValid() || !endDate.isValid()))
      ) {
        routerStore.push(`${routes.root}${routerStore.location.search}`);
      } else {
        const from = formatDate(startDate);
        const to = formatDate(endDate);
        const period = endDate.diff(startDate, 'days');

        const { data } = await API.get(
          APIRoutes.YACHT_AVAILABILITY(yachtIds, from, to),
        );

        if (startDate.isSame(new Date(), 'day')) {
          data.data.restrictions = `Rezerwacja rozpoczynająca się od dnia dzisiejszego musi być przez nas potwierdzona.
            Zostaw wiadomość, a skontaktujemy się najszybciej jak to możliwe.`;
          data.data.available = false;
        }

        runInAction(() => {
          this.summary = data.data;
          this.isBookingAvailable = data.data.bookingAvailable;
          bookingStore.price = data.data.price;
          bookingStore.offerId = data.data.offerId;
          // set selected by API yacht id
          calendarStore.yachtId = data.data.yachtId;
          calendarStore.startDate = moment(from);
          calendarStore.endDate = moment(to);
        });

        // SEND COMMERCE ANALYTICS DATA VIEW_ITEM
        const { data: booking } = data;

        const commercePayloadData = {
          currency: 'PLN',
          value: booking.price || 0,
          items: [
            {
              item_id: booking.yachtId,
              item_name: booking.yachtName,
              price: booking.price || 0,
              quantity: 1,
              charter_period: `${from} - ${to}`,
              charter_duration: period,
            },
          ],
        };

        logEvent(EVENTS_NAMES.COMMERCE_VIEW_ITEM, commercePayloadData);

        const payload = {
          action: POST_MESSAGE_KEYS.ANALYTICS_COMMERCE_VIEW_ITEM,
          iframeId: widgetStore.iframeId,
          data: commercePayloadData,
        };

        sendAnalyticsData(payload);
      }
    } catch (error) {
      console.warn(error); // eslint-disable-line
    } finally {
      runInAction(() => {
        widgetStore.isLoading = false;
        widgetStore.initialized = true;
      });
    }
  };

  sendQuestion = async (formData, setError) => {
    try {
      runInAction(() => {
        this.isLoading = true;
      });
      const { host } = widgetStore.queryParams;
      const startDate = formatDate(calendarStore.startDate);
      const endDate = formatDate(calendarStore.endDate);
      const period = calendarStore.endDate.diff(
        calendarStore.startDate,
        'days',
      );

      const { status } = await API.post(
        APIRoutes.SEND_QUESTION(calendarStore.yachtId),
        {
          message: {
            ...formData,
            date_from: startDate,
            date_to: endDate,
            url: host,
          },
        },
      );

      if (status === 204) {
        const payload = {
          action: POST_MESSAGE_KEYS.ANALYTICS_SEND_QUESTION,
          iframeId: widgetStore.iframeId,
          data: `Yacht id ${calendarStore.yachtId}: ${startDate} - ${endDate}`,
        };

        sendAnalyticsData(payload);

        toast.success('Zapytanie zostało wysłane');

        logEvent(EVENTS_NAMES.QUESTION_MESSAGE_SENT);

        const commercePayloadData = {
          currency: 'PLN',
          value: this.summary.price || 0,
          lead_source: this.summary.restrictions
            ? 'Custom period request'
            : 'Booking request',
          items: [
            {
              item_id: this.summary.yachtId,
              item_name: this.summary.yachtName,
              price: this.summary.price || 0,
              quantity: 1,
              charter_period: `${startDate} - ${endDate}`,
              charter_duration: period,
            },
          ],
        };

        // log commerce event
        logEvent(EVENTS_NAMES.COMMERCE_GENERATE_LEAD, commercePayloadData);

        // SEND CLIENT GA EVENT GENERATE_LEAD
        const commercePayload = {
          action: POST_MESSAGE_KEYS.ANALYTICS_COMMERCE_GENERATE_LEAD,
          iframeId: widgetStore.iframeId,
          data: commercePayloadData,
        };

        sendAnalyticsData(commercePayload);

        routerStore.push(`${routes.root}${routerStore.location.search}`);
        calendarStore.resetDates();
      }
    } catch (error) {
      const { errors } = error;
      if (errors) {
        Object.keys(errors).forEach(fieldName => {
          setError(fieldName, { message: errors[fieldName] });
        });
      }
      console.warn(error); // eslint-disable-line
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  changeDates = () => {
    routerStore.push(`${routes.root}${routerStore.location.search}`);
    if (RESET_CALENDAR_ON_CHANGE_DATES) {
      setTimeout(() => calendarStore.resetDates(), 100);
    }
  };

  reset = () => {
    runInAction(() => {
      this.summary = {};
    });
  };
}

export default new SummaryStore();
